'use client';
import {PropsWithChildren, useEffect, useState} from 'react';
import Image from 'next/image';

import guests from '../../../public/guests.png';
import MapPinIcon from '../Icons/MapPin';

import {IVillageDictionary} from '@/dictionaries/pages/village/types';
import {IRegionZoneDictonary} from '@/dictionaries/utils/region-zone/types';
import {Village} from '@/services/types';

type Dictionary = {
  regionZoneDict: IRegionZoneDictonary;
  villageDict: IVillageDictionary;
};

type Props = {
  videoModalIsOpen: boolean;
  galleryModalIsOpen: boolean;
  openModalCarousel: boolean;
  openVideoModal: () => void;
  openGalleryModal: () => void;
  closeVideoModal: () => void;
  closeGalleryModal: () => void;
  village: Village;
  dict: Dictionary;
} & PropsWithChildren;

export default function Modals({
  videoModalIsOpen,
  galleryModalIsOpen,
  openModalCarousel,
  openVideoModal,
  openGalleryModal,
  closeVideoModal,
  closeGalleryModal,
  village,
  dict,
  children,
}: Props) {
  const [heightRef, setHeightRef] = useState<number | undefined>(undefined);

  const {villageDict, regionZoneDict} = dict;
  const villageRegion = `${village.city} (${
    regionZoneDict[village.zone.code]
  })`;

  const galleryMedia = village.media.filter(
    (file) => file.type === 'gallery' || file.type === 'cover',
  );

  useEffect(() => {
    const ref = document
      .querySelector('#gallery-images')
      ?.getBoundingClientRect();
    setHeightRef(ref?.top);
    window.scrollTo({top: 0});
  }, [galleryModalIsOpen]);

  const handleScroll = () => {
    const galleryHeader = document.querySelector('#gallery-header');
    const scrollTop = document.querySelector('#gallery-dialog')?.scrollTop || 0;

    if (heightRef && scrollTop > heightRef) {
      galleryHeader?.classList.add('is-sticky');
    } else {
      galleryHeader?.classList.remove('is-sticky');
    }
  };

  return (
    <>
      {videoModalIsOpen && (
        <div
          role="dialog"
          className="fixed right-0 bottom-0 w-full h-full min-h-full bg-black z-[999] overflow-y-auto p-4">
          <div className="h-full flex flex-col">
            <div className="m-auto h-[calc(100%-120px)] w-full lg:w-[90%]">
              <iframe
                className="h-full w-full"
                frameBorder="0"
                allowFullScreen
                src={`https://www.youtube.com/embed/${village.videoUrl}`}
              />
            </div>
          </div>
          <button
            onClick={closeVideoModal}
            className="absolute top-4 right-4 z-50 cursor-pointer appearance-none">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-10 h-10 fill-current text-white"
              viewBox="0 -960 960 960">
              <path d="m256-168-88-88 224-224-224-224 88-88 224 224 224-224 88 88-224 224 224 224-88 88-224-224-224 224Z" />
            </svg>
          </button>
        </div>
      )}
      {galleryModalIsOpen && (
        <div
          id="gallery-dialog"
          onScroll={handleScroll}
          role="dialog"
          className="fixed right-0 bottom-0 w-full h-full min-h-full bg-white z-[999] overflow-y-auto p-4">
          {!openModalCarousel && (
            <>
              <button
                onClick={closeGalleryModal}
                className="fixed top-4 right-4 z-[999] cursor-pointer appearance-none">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-10 h-10 fill-current text-gray-500"
                  viewBox="0 -960 960 960">
                  <path d="m256-168-88-88 224-224-224-224 88-88 224 224 224-224 88 88-224 224 224 224-88 88-224-224-224 224Z" />
                </svg>
              </button>

              <div
                id="gallery-header"
                className="fixed right-0 bg-white -top-20 h-[70px] z-[998] flex justify-center items-center w-full transition-all duration-500 [&.is-sticky]:shadow-header [&.is-sticky]:translate-y-20 [&.is-sticky]:duration-1000">
                <h3 className="text-3xl mt-0 leading-[1.25] font-title">
                  {village.title}
                </h3>
              </div>
            </>
          )}

          <div className="mb-0 mx-auto py-[2.5rem] h-full max-w-[64rem] flex flex-col">
            <div className="pb-[2.5rem] text-center">
              <h3 className="text-[2rem] mt-0 mb-2 leading-[1.25] font-title">
                {village.title}
              </h3>
              <div className="flex justify-center items-center">
                <MapPinIcon className="w-5 h-5 mr-1 text-gray-700 relative top-[-5px]" />
                <h4 className="text-base mt-0 mb-2 text-center leading-[1.25]">
                  {villageRegion}
                </h4>
              </div>

              <div className="mt-auto flex flex-wrap items-start justify-center">
                <Image
                  width={22}
                  height={22}
                  src={guests}
                  alt="Guests icon image"
                />
                <span className="ml-2">{village.sleeps}</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-[22px] h-[22px] ml-8"
                  viewBox="0 0 24 20">
                  <path
                    fill="#CCC"
                    fillRule="evenodd"
                    d="M22 10.444c1.737.768 2 1.556 2 1.556v8h-2v-2H2v2H0v-8s.263-.788 2-1.556V0h2v3h16V0h2v10.444zm-2-.666V5h-7v4.009c3.138.054 5.389.366 7 .769zm-16 0c1.611-.403 3.862-.715 7-.77V5H4v4.778zM22 16v-3s-2-2-10-2-10 2-10 2v3h20z"
                  />
                </svg>
                <span className="ml-2">{village.bedrooms}</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-[22px] h-[22px] ml-8"
                  viewBox="0 0 24 20">
                  <path
                    fill="#CCC"
                    fillRule="nonzero"
                    d="M11 5.998a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm4 0a1 1 0 1 1 0-2 1 1 0 0 1 0 2zM13 9a1 1 0 1 1 0-2 1 1 0 0 1 0 2zM9 9a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm8 0a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm7 2v2h-2v1c0 1.89-1.235 3.584-3 4l1 2h-2l-1-2H7l-1 2H4l1-2c-1.765-.416-3-2.11-3-4v-1H0v-2h20V3c0-.551-.449-1-1-1h-5v1h-2V0h7c1.654 0 3 1.346 3 3v8h2zm-4 3v-1H4v1c0 1.102.897 2 2 2h12c1.103 0 2-.898 2-2z"
                  />
                </svg>

                <span className="ml-2">{village.bathrooms}</span>
              </div>
            </div>
            {children}
          </div>
        </div>
      )}
      <div className="pointer-events-none flex flex-col items-center justify-center absolute top-0 left-0 w-full h-full p-[30px] text-white desktop:hidden before:content-[''] before:absolute before:bottom-0 before:left-0 before:w-full before:h-[100px] before:bg-[linear-gradient(180deg,transparent,rgba(0,0,0,.6))] before:z-[1]">
        {village.videoUrl && (
          <div
            onClick={openVideoModal}
            className="cursor-pointer mt-auto pointer-events-auto w-[80px] h-[80px] flex items-center justify-center">
            <Image
              width={80}
              height={80}
              src="https://villatravellers-prod.s3.eu-west-2.amazonaws.com/assets/images/play.png"
              alt="View all"
            />
          </div>
        )}
        <button
          onClick={openGalleryModal}
          className="cursor-pointer w-full mt-auto pointer-events-auto flex items-center flex-wrap text-white z-10">
          <h2 className="mb-0 mt-0 text-title font-title">
            {galleryMedia.length} {villageDict.gallery.photos}
          </h2>
          <p className="mb-0 ml-auto font-semibold uppercase text-left leading-[1.7] tracking-[2px]">
            {villageDict.gallery.viewAll}
          </p>
        </button>
      </div>
    </>
  );
}
